(() => {
  const compact = (root, selector) => {
    const choices = [...root.querySelectorAll(selector)].filter(e => !e.hasAttribute('data-z-colour-more'));
    if (choices.length < 2) return;
    let more = root.querySelector('[data-z-colour-more]');
    if (!more) {
      more = document.createElement('button'); more.type = 'button'; more.dataset.zColourMore = '';
      more.className = 'z-colour-more';
      more.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        root.dataset.expanded = String(root.dataset.expanded !== 'true'); update();
      });
      root.append(more);
    }
    const update = () => {
      const cardWidth = root.closest('.grid__item,.zv7-card')?.clientWidth || Infinity;
      const width = Math.min(root.clientWidth || 240,cardWidth);
      const limit = Math.min(choices.length, Math.max(1, Math.min(4, Math.floor(width / 44) - 1)));
      const shown = choices.slice(0, limit);
      const selected = choices.find(e => e.getAttribute('aria-current') === 'true' || e.getAttribute('aria-pressed') === 'true');
      if (selected && !shown.includes(selected)) shown[shown.length - 1] = selected;
      const expanded = root.dataset.expanded === 'true';
      choices.forEach(e => { e.hidden = !expanded && !shown.includes(e); });
      more.hidden = choices.length <= limit;
      more.textContent = expanded ? '-' : '+' + (choices.length - limit);
      more.setAttribute('aria-expanded', String(expanded));
      more.setAttribute('aria-label', expanded ? 'Show fewer colours' : 'Show ' + (choices.length - limit) + ' more colours');
      root.classList.add('z-colours-compact');
    };
    root._zCompactUpdate = update;
    root._zCompactObserver ||= new ResizeObserver(() => root._zCompactUpdate());
    root._zCompactObserver.observe(root); update();
  };
  const preload = new Map();
  const ready = src => {
    if (!preload.has(src)) {
      const img = new Image(); img.src = src;
      preload.set(src, img.decode().catch(error => { preload.delete(src); throw error; }));
    }
    return preload.get(src);
  };
  const preview = (card, image, secondary, choices) => {
    let original, version = 0;
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    const restore = () => {
      version++;
      if (!original) return;
      for (const [key, value] of Object.entries(original.attributes)) value === null ? image.removeAttribute(key) : image.setAttribute(key, value);
      if (secondary) original.secondaryStyle === null ? secondary.removeAttribute('style') : secondary.setAttribute('style', original.secondaryStyle);
      delete card.dataset.zColourPreview; original = null;
    };
    for (const {control, image:src} of choices) {
      if (!src) continue;
      const show = async () => {
        const ticket = ++version;
        try { await ready(src); } catch { return; }
        if (ticket !== version || !card.isConnected) return;
        original ||= {attributes:Object.fromEntries(['src','srcset','style','alt'].map(key => [key,image.getAttribute(key)])), secondaryStyle:secondary?.getAttribute('style') ?? null};
        image.removeAttribute('srcset'); image.src = src; image.style.opacity = '1';
        if (secondary) secondary.style.opacity = '0';
        card.dataset.zColourPreview = 'true';
      };
      control.addEventListener('pointerenter', event => {
        if (finePointer.matches && event.pointerType === 'mouse') show();
      });
      control.addEventListener('focus', () => {
        if (finePointer.matches && control.matches(':focus-visible')) show();
      });
      control.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse') restore();
      });
      control.addEventListener('blur', restore);
    }
    return restore;
  };
  const selections = new WeakMap();
  let idSerial = 0;
  const productPath = pathname => {
    const root = new URL(window.Shopify?.routes?.root || '/', location.origin).pathname.replace(/\/?$/, '/');
    const prefix = root + 'products/';
    if (pathname.startsWith(prefix)) {
      const handle = pathname.slice(prefix.length).replace(/\/$/, '');
      return Boolean(handle) && !handle.includes('/');
    }
    return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(pathname);
  };
  const selectionURL = (value, control) => {
    let url;
    try { url = new URL(value, location.href); } catch { return null; }
    if (url.origin !== location.origin || !productPath(url.pathname)) return null;
    const variant = url.searchParams.get('variant') || control?.dataset.variant;
    if (!/^\d+$/.test(variant || '')) return null;
    url.searchParams.set('variant', variant);
    const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    url.searchParams.set('section_id', 'zico-saved-card');
    url.hash = '';
    return url;
  };
  const productData = source => {
    const node = source.querySelector('[data-zico-card-product]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (_) { return null; }
  };
  const matchesSelection = (source, selection) => {
    let handle;
    try { handle = decodeURIComponent(selection.pathname.replace(/\/$/, '').split('/').pop()).trim().toLowerCase(); } catch (_) { return false; }
    const variant = selection.searchParams.get('variant');
    const wish = source.querySelector('[data-z-wish]');
    const product = productData(source);
    const handles = [source.dataset.zHandle, wish?.dataset.handle, product?.handle].filter(value => value !== undefined && value !== null && value !== '');
    if (!handle || !handles.length || !handles.every(value => String(value).trim().toLowerCase() === handle)) return false;
    const wishVariant = wish?.dataset.variant;
    if (wishVariant !== undefined && wishVariant !== '' && String(wishVariant) !== variant) return false;
    const variants = Array.isArray(product?.variants) ? product.variants.map(item => String(item?.id)).filter(Boolean) : [];
    if (variants.length && !variants.includes(variant)) return false;
    return Boolean((wishVariant !== undefined && wishVariant !== '') || variants.length);
  };
  const showFailure = card => {
    let message = card.querySelector('[data-z-card-colour-error]');
    if (!message) {
      message = document.createElement('p');
      message.dataset.zCardColourError = '';
      message.className = 'z-card-colour-error';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      card.append(message);
    }
    message.hidden = false;
    message.textContent = 'Could not load this colour. Please try again.';
  };
  const clearFailure = card => {
    const message = card.querySelector('[data-z-card-colour-error]');
    if (message) { message.hidden = true; message.textContent = ''; }
  };
  const mediaImages = card => {
    const images = [...card.querySelectorAll('.card__media .media > img, .card__media img')];
    return images.length ? images : [...card.querySelectorAll('img')];
  };
  const decodeMedia = card => Promise.all(mediaImages(card).slice(0, 2).map(image => {
    image.setAttribute('loading', 'eager');
    return typeof image.decode === 'function' ? image.decode() : Promise.resolve();
  }));
  const remapIds = card => {
    const suffix = `-card-colour-${++idSerial}`, ids = new Map();
    [card, ...card.querySelectorAll('[id]')].filter(node => node.id).forEach(node => {
      ids.set(node.id, node.id + suffix); node.id += suffix;
    });
    card.querySelectorAll('*').forEach(node => {
      for (const attribute of ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls']) {
        const value = node.getAttribute(attribute);
        if (value) node.setAttribute(attribute, value.split(' ').map(id => ids.get(id) || id).join(' '));
      }
      for (const attribute of ['href', 'data-modal', 'data-target']) {
        const value = node.getAttribute(attribute);
        if (value) node.setAttribute(attribute, value.replace(/#([A-Za-z][\w:.-]*)/g, (match, id) => ids.has(id) ? '#' + ids.get(id) : match));
      }
    });
  };
  const selectedHref = (href, selection) => {
    let url;
    try { url = new URL(href || selection.href, location.href); } catch { return null; }
    if (url.origin !== location.origin || !productPath(url.pathname)) return null;
    url.searchParams.set('variant', selection.searchParams.get('variant'));
    const previewTheme = selection.searchParams.get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    url.searchParams.delete('section_id');
    url.hash = '';
    return url.href;
  };
  const syncProductLinks = (card, selection) => card.querySelectorAll('a[href]').forEach(link => {
    if (link.closest('.z-home-colours')) return;
    const href = selectedHref(link.getAttribute('href'), selection);
    if (href) link.href = href;
  });
  const copyImage = (source, target) => {
    if (!source || !target) return;
    for (const name of ['src', 'srcset', 'alt', 'width', 'height', 'sizes', 'loading']) {
      const value = source.getAttribute(name);
      value === null ? target.removeAttribute(name) : target.setAttribute(name, value);
    }
    target.hidden = source.hidden;
  };
  const copyContents = (target, source) => {
    if (!target) return;
    target.replaceChildren(...(source ? [...source.childNodes].map(node => document.importNode(node, true)) : []));
  };
  const commitLegacy = (card, source, selection) => {
    const sourceImages = mediaImages(source);
    const primary = card.querySelector('.zv7-card__primary');
    let secondary = card.querySelector('.zv7-card__secondary');
    if (!secondary && sourceImages[1] && primary) {
      secondary = document.createElement('img');
      secondary.className = 'zv7-card__secondary';
      primary.after(secondary);
      card.querySelector('.zv7-card__media')?.classList.add('zv7-card__media--swap');
    }
    copyImage(sourceImages[0], primary);
    if (secondary) {
      if (sourceImages[1]) { copyImage(sourceImages[1], secondary); secondary.hidden = false; }
      else secondary.hidden = true;
    }
    const sourceTitle = source.querySelector('.card__heading');
    const title = card.querySelector('.zv7-card__title');
    if (sourceTitle && title) title.textContent = sourceTitle.textContent.trim();
    const sourceColour = source.querySelector('.zico-card-color');
    const colour = card.querySelector('.zv7-card__color');
    if (sourceColour && colour) colour.textContent = sourceColour.textContent.trim();
    copyContents(card.querySelector('.zv7-card__price'), source.querySelector('.price'));
    copyContents(card.querySelector('.zv7-card__badge'), source.querySelector('.card__badge'));
    const sourceLink = source.querySelector('a[href*="/products/"]');
    const link = card.querySelector('.zv7-card__link');
    const href = selectedHref(sourceLink?.getAttribute('href'), selection);
    if (link && href) link.href = href;
    const sourceWish = source.querySelector('[data-z-wish]');
    const wish = card.querySelector('[data-z-wish]');
    for (const name of ['zProductId', 'zHandle', 'zProductColour']) {
      if (source.dataset[name] !== undefined) card.dataset[name] = source.dataset[name];
    }
    if (sourceWish && wish) for (const name of ['data-handle', 'data-variant', 'data-colour', 'data-color', 'data-product-id', 'data-z-wish-title']) {
      if (sourceWish.hasAttribute(name)) wish.setAttribute(name, sourceWish.getAttribute(name));
    }
    syncProductLinks(card, selection);
    return card;
  };
  const ownsFocus = control => document.activeElement === control || control.contains(document.activeElement);
  const matchingControl = (card, selection, control) => {
    const variant = selection.searchParams.get('variant');
    const label = control.getAttribute('aria-label') || control.title;
    for (const family of card.querySelectorAll('z-colour-family')) {
      const buttons = [...family.querySelectorAll('button[data-z-colour-option]')];
      const entryIndex = Array.isArray(family.entries) ? family.entries.findIndex(entry => {
        let sameURL = false;
        try {
          const entryURL = new URL(entry.url, location.href);
          sameURL = entryURL.pathname === selection.pathname && entryURL.searchParams.get('variant') === variant;
        } catch (_) {}
        return sameURL || entry.color === label || entry.colour === label;
      }) : -1;
      if (entryIndex >= 0 && buttons[entryIndex]) return buttons[entryIndex];
      const labelled = buttons.find(item => item.dataset.variant === variant || (label && (item.getAttribute('aria-label') === label || item.title === label)));
      if (labelled) return labelled;
    }
    const anchors = card.matches?.('.z-home-colours') ? card.querySelectorAll('a') : card.querySelectorAll('.z-home-colours a');
    return [...anchors].find(item => {
      try { return new URL(item.href, location.href).searchParams.get('variant') === variant; } catch { return false; }
    });
  };
  const markHomeCurrent = (card, selection) => card.querySelectorAll('.z-home-colours').forEach(root => {
    const selected = matchingControl(root, selection, document.createElement('span'));
    if (!selected) return;
    root.querySelectorAll('a').forEach(item => item === selected ? item.setAttribute('aria-current', 'true') : item.removeAttribute('aria-current'));
  });
  const notifyCardChange = card => {
    card.dispatchEvent(new CustomEvent('zico:media-changed', {bubbles: true, detail: {card}}));
    document.dispatchEvent(new CustomEvent('zico:card-colour-change', {detail: {card}}));
    document.dispatchEvent(new CustomEvent('zico:products-appended', {detail: {scope: card.parentElement, items: [card]}}));
  };
  const selectCard = async (card, control, url, {restorePreview} = {}) => {
    try { restorePreview?.(); } catch (_) {}
    if (!card?.isConnected || !control) return null;
    const selection = selectionURL(url, control);
    if (!selection) { showFailure(card); return null; }
    const prior = selections.get(card);
    prior?.controller?.abort();
    const state = {ticket: (prior?.ticket || 0) + 1, controller: new AbortController(), timedOut: false};
    selections.set(card, state);
    const current = () => selections.get(card) === state && card.isConnected && !state.timedOut;
    const timeout = window.setTimeout(() => {
      if (selections.get(card) !== state || !card.isConnected) return;
      state.timedOut = true;
      state.controller.abort();
      card.removeAttribute('aria-busy');
      showFailure(card);
    }, 12000);
    card.setAttribute('aria-busy', 'true');
    clearFailure(card);
    try {
      const response = await window.fetch(selection.href, {signal: state.controller.signal});
      if (!response.ok) throw Error('Product unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = html.querySelector('[data-z-saved-card] product-component');
      if (!source || !matchesSelection(source, selection)) throw Error('Product unavailable');
      const next = document.importNode(source, true);
      await decodeMedia(next);
      if (!current()) return null;
      const focusOwned = ownsFocus(control);
      const coloursExpanded = control.closest('.z-colour-family__links,.z-home-colours')?.dataset.expanded === 'true';
      let updated;
      if (card.matches('.zv7-card')) updated = commitLegacy(card, next, selection);
      else {
        remapIds(next);
        syncProductLinks(next, selection);
        card.replaceWith(next);
        updated = next;
      }
      clearFailure(updated);
      markHomeCurrent(updated, selection);
      notifyCardChange(updated);
      await Promise.resolve();
      await updated.querySelector('z-colour-family')?.renderPromise?.catch(() => null);
      if (coloursExpanded) updated.querySelectorAll('.z-colour-family__links,.z-home-colours').forEach(root => {
        root.dataset.expanded = 'true';
        root._zCompactUpdate?.();
      });
      if (focusOwned && (document.activeElement === control || document.activeElement === document.body)) matchingControl(updated, selection, control)?.focus({preventScroll: true});
      return updated;
    } catch (_) {
      if (current()) showFailure(card);
      return null;
    } finally {
      window.clearTimeout(timeout);
      if (selections.get(card) === state) card.removeAttribute('aria-busy');
    }
  };
  const mount = scope => {
    homes(scope);
  };
  window.ZicoCardColours = {compact, preview, ready, selectCard};
  const homes = scope => scope.querySelectorAll('.z-home-colours').forEach(root => {
    if (root.dataset.zColourUi) return; root.dataset.zColourUi = 'true';
    compact(root, 'a');
    const card = root.closest('product-component,.zv7-card'), image = card?.querySelector('.card__media .media > img,.zv7-card__primary');
    const restore = image ? preview(card, image, card.querySelector('.card__media .media > img:nth-child(2),.zv7-card__secondary'), [...root.querySelectorAll('a')].map(control => ({control,image:control.dataset.primary}))) : undefined;
    const activate = control => selectCard(card, control, control.href, {restorePreview: restore});
    const modified = event => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    root.addEventListener('click', event => {
      const control = event.target.closest('a');
      if (!control || !root.contains(control) || event.button > 0 || modified(event)) return;
      event.preventDefault(); activate(control);
    });
    root.addEventListener('keydown', event => {
      const control = event.target.closest('a');
      if (!control || !root.contains(control) || modified(event) || !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
      event.preventDefault(); activate(control);
    });
  });
  mount(document);
  document.addEventListener('zico:products-appended', event => { if (event.detail?.scope) mount(event.detail.scope); });
  document.addEventListener('shopify:section:load', event => mount(event.target));
  const content=document.getElementById('MainContent');
  if(content)new MutationObserver(records=>records.forEach(record=>record.addedNodes.forEach(node=>{if(node.nodeType===1)mount(node);}))).observe(content,{childList:true,subtree:true});
})();





